'use client';
import React from 'react';

interface DestinationCardProps {
  name: string;
  image: string;
  alt: string;
  onClick?: () => void;
  wide?: boolean;
  price: string;
  saving: string;
  iata: string;
}

function DestinationCard({
  name,
  image,
  alt,
  onClick,
  wide,
  price,
  saving,
  iata,
}: DestinationCardProps) {
  return (
    <div
      className={`relative overflow-hidden cursor-pointer rounded-3xl group border border-slate-200/50 shadow-[0_4px_25px_rgba(0,0,0,0.03)] hover:shadow-premium transition-all duration-500 hover:-translate-y-1.5 ${
        wide ? 'col-span-4 md:col-span-2' : 'col-span-4 sm:col-span-2 md:col-span-1'
      }`}
      style={{ height: '260px' }}
      onClick={onClick}
    >
      <img
        src={image}
        alt={alt}
        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
      />

      {/* Modern gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-900/25 to-transparent group-hover:via-slate-950/45 transition-all duration-500"></div>

      {/* Top Floating Glass Badge: Discount Saving */}
      <div className="absolute top-4 left-4 bg-slate-950/60 backdrop-blur-md px-3 py-1 rounded-full border border-white/10 flex items-center gap-1.5 shadow-md">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
        <span className="text-[9px] font-black text-emerald-300 uppercase tracking-widest">
          SAVE {saving}
        </span>
      </div>

      {/* Floating IATA Code Badge in Top-Right */}
      <div className="absolute top-4 right-4 bg-white/10 backdrop-blur-sm w-8 h-8 rounded-full border border-white/10 flex items-center justify-center shadow-md">
        <span className="text-[9px] font-black text-white uppercase tracking-wider">{iata}</span>
      </div>

      {/* Floating bottom glass city name & price badge */}
      <div className="absolute bottom-4 left-4 right-4 bg-slate-950/65 backdrop-blur-lg px-4 py-3 rounded-2xl border border-white/10 flex items-center justify-between transition-all duration-300 group-hover:bg-accent/95 group-hover:border-accent/10 shadow-premium">
        <div>
          <span className="text-[10px] text-slate-400 group-hover:text-white/80 transition-colors font-black uppercase tracking-wider block">
            FLY TO
          </span>
          <span className="text-white text-sm font-extrabold tracking-wider uppercase font-display block mt-0.5">
            {name}
          </span>
        </div>
        <div className="text-right">
          <span className="text-[9px] text-slate-400 group-hover:text-white/80 transition-colors font-black uppercase tracking-wider block">
            FARES FROM
          </span>
          <span className="text-accent group-hover:text-white text-base font-black tracking-wider block mt-0.5 transition-colors">
            {price}
          </span>
        </div>
      </div>
    </div>
  );
}

const destinations = [
  {
    name: 'Los Angeles',
    image:
      'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?auto=format&fit=crop&w=1200&q=80',
    alt: 'Sunset over palm trees in Los Angeles California',
    price: '$189',
    saving: '55%',
    iata: 'LAX',
  },
  {
    name: 'Orlando',
    image:
      'https://images.unsplash.com/photo-1597466765990-64ad1c35dafc?auto=format&fit=crop&w=800&q=80',
    alt: 'Vibrant amusement theme park landmark in Orlando Florida',
    price: '$139',
    saving: '50%',
    iata: 'MCO',
  },
  {
    name: 'Atlanta',
    image:
      'https://images.unsplash.com/photo-1506157786151-b8491531f063?auto=format&fit=crop&w=800&q=80',
    alt: 'Skyscrapers in Atlanta Georgia under blue sky',
    price: '$169',
    saving: '48%',
    iata: 'ATL',
  },
  {
    name: 'Dallas',
    image:
      'https://images.unsplash.com/photo-1547989453-11e67ffb3885?auto=format&fit=crop&w=800&q=80',
    alt: 'Dallas skyline illumination at night',
    price: '$179',
    saving: '52%',
    iata: 'DFW',
  },
  {
    name: 'New York',
    image:
      'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?auto=format&fit=crop&w=1200&q=80',
    alt: 'Classic Brooklyn bridge skyline view of Manhattan New York',
    price: '$129',
    saving: '58%',
    iata: 'JFK',
    wide: true,
  },
  {
    name: 'Miami',
    image:
      'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?auto=format&fit=crop&w=1200&q=80',
    alt: 'Sunset over palm trees in Miami Florida',
    price: '$139',
    saving: '52%',
    iata: 'MIA',
    wide: true,
  },
];

interface DestinationsSectionProps {
  onDestinationClick?: (name: string) => void;
}

export default function DestinationsSection({ onDestinationClick }: DestinationsSectionProps) {
  return (
    <div className="bg-slate-50 py-20 border-b border-slate-100">
      <div className="max-w-[1170px] mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div
            className="inline-flex items-center gap-1.5 bg-accent/15 border border-accent/30 px-3.5 py-1.2 rounded-full text-[10px] font-black text-accent uppercase tracking-widest mb-4 shadow-sm"
            style={{ height: '30px' }}
          >
            WORLDWIDE FLIGHT EXPLORER
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-none mb-4 uppercase">
            Exclusive Flight Deals
          </h2>
          <p className="text-slate-500 text-xs md:text-sm font-semibold leading-relaxed">
            Browse our top selected destinations featuring direct, unpublished wholesale airfares up
            to 55% lower than retail.
          </p>
        </div>

        {/* Dynamic Grid */}
        <div className="grid grid-cols-4 gap-6">
          {destinations.map((dest) => (
            <DestinationCard
              key={dest.name}
              name={dest.name}
              image={dest.image}
              alt={dest.alt}
              wide={dest.wide}
              price={dest.price}
              saving={dest.saving}
              iata={dest.iata}
              onClick={() => onDestinationClick?.(dest.name)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
